
import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';
export type ThemePreset = 'clinical-teal' | 'executive-navy' | 'command-emerald' | 'midnight-slate';

export const THEME_PRESETS: Array<{ id: ThemePreset; label: string }> = [
  { id: 'clinical-teal', label: 'Clinical Teal' },
  { id: 'executive-navy', label: 'Executive Navy' },
  { id: 'command-emerald', label: 'Command Emerald' },
  { id: 'midnight-slate', label: 'Midnight Slate' },
];

interface ThemeContextType {
  theme: Theme;
  themePreset: ThemePreset;
  toggleTheme: () => void;
  cycleThemePreset: () => void;
  setThemePreset: (preset: ThemePreset) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check local storage or system preference
    if (localStorage.getItem('theme')) {
      return localStorage.getItem('theme') as Theme;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const [themePreset, setThemePreset] = useState<ThemePreset>(() => {
    const stored = localStorage.getItem('themePreset') as ThemePreset | null;
    if (stored && THEME_PRESETS.some((preset) => preset.id === stored)) {
      return stored;
    }
    return 'clinical-teal';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.setAttribute('data-theme-preset', themePreset);
    localStorage.setItem('themePreset', themePreset);
  }, [themePreset]);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const cycleThemePreset = () => {
    setThemePreset((current) => {
      const currentIndex = THEME_PRESETS.findIndex((preset) => preset.id === current);
      const next = THEME_PRESETS[(currentIndex + 1) % THEME_PRESETS.length];
      return next.id;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, themePreset, toggleTheme, cycleThemePreset, setThemePreset }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
